import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Brain, ArrowClockwise } from '@phosphor-icons/react';
import api from '../lib/api';

/**
 * Admin-only "Objection Memory" widget — AI-analyzed themes of what prospects
 * push back on in the public Crunchy sales/migration chats. On-demand generation
 * (one Claude call over the last ~100 conversations), cached in objection_reports.
 */
export default function ObjectionMemoryWidget() {
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get('/ai/admin/objection-report/latest')
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const r = await api.post('/ai/admin/objection-report');
      if (!r.data.report) {
        toast.info(r.data.note || 'No conversations to analyze yet.');
      } else {
        setData(r.data);
        toast.success(`Analyzed ${r.data.conversations_analyzed} conversations`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Analysis failed');
    } finally {
      setGenerating(false);
    }
  };

  const report = data?.report;

  return (
    <div className="border border-slate-200 bg-white rounded-sm mb-4" data-testid="objection-memory-widget">
      <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Brain size={18} weight="bold" className="text-[#0B5394]" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#111827]">
            Objection Memory
          </h3>
          {data?.created_at && (
            <span className="text-[10px] text-[#9CA3AF]">
              last analyzed {data.created_at.slice(0, 10)} · {data.conversations_analyzed} chats
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={generating}
          className="bg-[#0B5394] hover:bg-[#094180] disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1.5"
          data-testid="objection-analyze-btn"
        >
          <ArrowClockwise size={12} weight="bold" className={generating ? 'animate-spin' : ''} />
          {generating ? 'Analyzing…' : report ? 'Re-analyze' : 'Analyze now'}
        </button>
      </div>

      {!report ? (
        <div className="px-4 py-5 text-center text-xs text-[#6B7280]" data-testid="objection-empty">
          No report yet. Click <strong>Analyze now</strong> and the AI will read the
          recent public Crunchy sales chats and rank the top reasons prospects hesitate.
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {(report.insights || []).length > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded p-3 text-[11px] text-[#0B5394] space-y-1">
              {report.insights.map((ins, i) => (
                <div key={i}>• {ins}</div>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {(report.objections || []).map((o, i) => (
              <div key={i} className="border border-slate-200 rounded p-3" data-testid="objection-row">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs font-bold text-slate-900">
                    {i + 1}. {o.theme}
                  </span>
                  <span className="flex items-center gap-1.5">
                    {(o.products || []).map((p) => (
                      <span key={p} className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${p.includes('Migration') ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                        {p.replace('CRM', '')}
                      </span>
                    ))}
                    <span className="text-[10px] text-[#6B7280] font-mono">×{o.count}</span>
                  </span>
                </div>
                {(o.example_quotes || []).map((q, qi) => (
                  <div key={qi} className="text-[11px] text-[#6B7280] italic">&ldquo;{q}&rdquo;</div>
                ))}
                {o.suggested_fix && (
                  <div className="text-[11px] text-emerald-700 mt-1">
                    <strong>Fix:</strong> {o.suggested_fix}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
